import { canUseCompanyContext } from "services/utils/companyAccess";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import moment from "moment";
import { theme } from "services/constants";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { deviceStatusReportUrl } from "services/utils/deviceStatusReport";
import { FilterProvider } from "pages/FilterContext";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { checkViewerPermission, subModuleNameMatches } from "services/utils/HelperFunction";
import { DeviceStatusReportHeader } from "pages/TeamZone/TeamZoneHeaders";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

export const DeviceStatusReport = ({
  setShowReport,
  setReportHeaderActions,
  embeddedReportTypes = null,
  selectedReportType = null,
  onReportTypeChange = null,
}) => {
  const { domain, token, userDetails, topbarCompanyId, userScreenAccess, topbarCompanyName } =
    useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [deviceStatusDetails, setDeviceStatusDetails] = useState([]);
  const [filteredDeviceStatusData, setFilteredDeviceStatusData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  // Real payloads return "Device Status" (no "Report" suffix) for this
  // screen's sub_module_name — subModuleNameMatches applies the same alias
  // table checkViewerPermission below relies on, instead of a raw === that
  // would silently land on undefined and send an invalid submodule id when
  // saving a view.
  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Device Status Report"),
  )?.sub_module_id;

  const checkAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Device Status Report",
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "device_status_report_saved_views",
    fullHeaders: DeviceStatusReportHeader,
    screenAccess: checkAccess,
    onViewChange: handleSavedViewChange,
  });

  // Server-side paginated: the API returns a single already-sliced page
  // (page/page_size in the query), so this effect legitimately depends on
  // page/rowsPerPage too — unlike every other client-paginated report in
  // this migration, changing page here has to trigger a real re-fetch.
  useEffect(() => {
    const fetchDeviceStatusDetails = async () => {
      try {
        setIsLoading(true);
        if (!canUseCompanyContext(topbarCompanyId, userDetails?.type)) {
          setDeviceStatusDetails([]);
          setFilteredDeviceStatusData([]);
          setTotalCount(0);
          return;
        }
        const response = await fetch(
          deviceStatusReportUrl(domain, {
            startDate, endDate, companyId: topbarCompanyId,
            page: page + 1, pageSize: rowsPerPage,
          }),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();

        if (response.ok) {
          const reportData = Array.isArray(res?.results) ? res.results : [];
          setDeviceStatusDetails(reportData);
          setFilteredDeviceStatusData(reportData);
          setTotalCount(res?.pagination?.total_count || 0);
        } else if (response.status === 409) {
          setDeviceStatusDetails([]);
          setFilteredDeviceStatusData([]);
          setTotalCount(0);
          sessionHandling();
        } else {
          setDeviceStatusDetails([]);
          setFilteredDeviceStatusData([]);
          setTotalCount(0);
          throw new Error(res.error || "Failed to load device status report");
        }
      } catch (error) {
        setDeviceStatusDetails([]);
        setFilteredDeviceStatusData([]);
        setTotalCount(0);
        showToast("error", error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeviceStatusDetails();
  }, [domain, endDate, page, rowsPerPage, startDate, token, topbarCompanyId, userDetails?.type]);

  const handleFilteredDataChange = useCallback((filteredData) => {
    setFilteredDeviceStatusData(filteredData);
  }, []);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartDate = (rangeStart, rangeEnd, rangeLabel) => {
    const parsedStartDate = moment(rangeStart);
    const parsedEndDate = moment(rangeEnd);

    if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
      return;
    }

    setStartDate(parsedStartDate.format("YYYY-MM-DD"));
    setEndDate(parsedEndDate.format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const formattedEndDate = moment(
      endDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");

    return {
      "Company Name": topbarCompanyName || "All",
      "Date Range": `${formattedStartDate} to ${formattedEndDate}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      "Overall Total Count": totalCount || 0,
    };
  }, [endDate, totalCount, startDate, topbarCompanyName]);

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType ||
        option?.value === selectedReportType,
    ) || null;

  const headerFilterControls = useMemo(
    () => (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          thisMonthToDate
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleStartDate}
          showToday={true}
          includeCurrentDate={true}
          allowFutureDate={false}
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        {embeddedReportTypes?.length ? (
          <CompactHeaderSelectFilter
            dataList={embeddedReportTypes}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
      </Mui.Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      dateRangeLabel,
      embeddedReportTypes,
      selectedReportTypeOption,
      onReportTypeChange,
    ],
  );

  // Filters render inline in the page body (matching Employee Attendance's
  // own row below the title), not floated beside the title.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const formatCellValue = (header, device) => {
    if (header.key === "status") {
      return (
        <Mui.Chip
          label={device?.status || "-"}
          size="small"
          variant="outlined"
          sx={{
            backgroundColor: `${device?.status_color || "#9e9e9e"}20`,
            borderColor: device?.status_color || "#9e9e9e",
            color: device?.status_color || "#9e9e9e",
            fontWeight: 600,
            // Compact pill: was 24px tall with an 84px minimum width.
            height: 22,
            fontSize: 12,
            "& .MuiChip-label": {
              color: device?.status_color || "#9e9e9e",
              px: 1,
            },
          }}
        />
      );
    }

    if (header.key === "last_status_changed_local") {
      return device?.last_status_changed_local || "-";
    }

    return device?.[header.key] || "-";
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          {/* Filters sit in their own row directly under the page title —
              same placement as Employee Attendance Report — instead of
              floating beside the title via setReportHeaderActions. */}
          <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            {headerFilterControls}
          </Mui.Box>
          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Device Status"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={deviceStatusDetails}
              filteredData={filteredDeviceStatusData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={checkAccess}
              showtitle_count={true}
              hideCount={true}
              isAttendanceScreenReport={true}
              showDownloadOptions={false}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              labeledToolbar={true}
              searchPlaceholder="Search by device, location..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            {isLoading ? (
              <Loader info="Loading..." />
            ) : (
              <Mui.Box>
                <Mui.TableContainer
                  sx={{
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: { xs: "auto", sm: "visible" },
                    overflowY: { xs: "auto", sm: "visible" },
                    pb: footerHeight ? `${footerHeight}px` : 0,
                    borderLeft: `1px solid ${designColors.slate200}`,
                    borderRight: `1px solid ${designColors.slate200}`,
                    borderBottom: `1px solid ${designColors.slate200}`,
                  }}
                >
                  <ApproveTable stickyHeader sx={{ minWidth: 860, width: "100%" }}>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        {visibleHeaders.map((header, headerIndex) => (
                          <Mui.TableCell
                            key={header.key}
                            sx={{
                              ...approveTableHeadCell,
                              top: APP_BAR_OFFSET + customFilterHeight,
                              ...(headerIndex === 0
                                ? { borderTopLeftRadius: 0 }
                                : {}),
                              ...(headerIndex === visibleHeaders.length - 1
                                ? { borderTopRightRadius: 0 }
                                : {}),
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {filteredDeviceStatusData?.length === 0 ? (
                        <TableEmptyState dataLength={deviceStatusDetails} />
                      ) : (
                        // The server already returns a single page, so
                        // render every row as-is (no client-side slicing).
                        filteredDeviceStatusData?.map((device, index) => (
                          <Mui.TableRow
                            key={`${device?.device_id || "device"}-${index}`}
                            hover
                            sx={{
                              ...designShell.tableRowHover,
                              "&:last-child td": { borderBottom: "none" },
                              "& td": { backgroundColor: designColors.white },
                            }}
                          >
                            {visibleHeaders.map((header) => (
                              <Mui.TableCell
                                key={`${header.key}-${index}`}
                                sx={approveTableBodyCell}
                              >
                                {formatCellValue(header, device)}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        ))
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
              </Mui.Box>
            )}

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: totalCount || 0,
                itemLabel: "devices",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
